import { faCalendar } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { format, isValid, parse, startOfDay } from "date-fns"
import { useState } from "react"
import type { ComponentProps } from "react"
import { cn } from "cn"

import { Calendar } from "../calendar/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "../popover/popover"

const DATE_FORMAT = "dd.MM.yyyy"

/** Tippt der Nutzer Ziffern, werden Punkte nach TT/MM automatisch eingefügt (max. TT.MM.JJJJ). */
function maskDateInput(raw: string) {
  const digits = raw.replace(/\D/g, "").slice(0, 8)
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
    .filter(Boolean)
    .join(".")
}

export type InputDatePickerProps = Omit<
  ComponentProps<"input">,
  "value" | "onChange" | "type"
> & {
  value?: Date
  onChange?: (date: Date | undefined) => void
}

/**
 * Figma-Component-Set „Input Date Picker" (node 2590:2824). Anders als das
 * bestehende bare `DatePicker` (reiner Popover-Button ohne Texteingabe)
 * erlaubt dieses Feld zusätzlich manuelle Tastatureingabe (Figma-State
 * „focus" zeigt einen Textcursor im Feld) — bewusst eine eigene Komponente
 * statt eines Umbaus von `DatePicker` (Nutzer-Entscheidung), da beide
 * Interaktionsmodelle in der App gebraucht werden.
 *
 * Wie `DatePicker` schaltet `Popover` auf Mobile automatisch auf `Drawer`
 * um (siehe popover.tsx) — deckt Figmas Mobile-Section „Drawer Date Picker"
 * ohne eigene Verzweigung hier ab.
 */
function InputDatePicker({
  className,
  value,
  onChange,
  disabled,
  id,
  placeholder = "TT.MM.JJJJ",
  "aria-invalid": ariaInvalid,
  ...props
}: InputDatePickerProps) {
  const [open, setOpen] = useState(false)
  const [text, setText] = useState(() =>
    value ? format(value, DATE_FORMAT) : ""
  )
  const [prevValue, setPrevValue] = useState(value)
  const [calendarMonth, setCalendarMonth] = useState(() => value ?? new Date())

  // Extern gesetzter/geänderter Wert (z. B. Formular-Reset) synchron ins Textfeld übernehmen —
  // bewusst während des Renders (React „Adjusting state when a prop changes"-Pattern), nicht in
  // einem Effect. Verglichen wird per Zeitstempel, da Konsumenten oft bei jedem Render ein neues
  // `Date` für denselben Tag erzeugen (z. B. `dayjs(iso).toDate()`) — sonst würde jede Eingabe
  // beim nächsten Re-Render überschrieben.
  if (value?.getTime() !== prevValue?.getTime()) {
    setPrevValue(value)
    setText(value ? format(value, DATE_FORMAT) : "")
  }

  /** Meldet den Wert nach außen und merkt ihn sich, damit der Rückfluss über `value` den Text nicht zurücksetzt. */
  function emit(next: Date | undefined) {
    if (next?.getTime() === value?.getTime()) {
      return
    }
    setPrevValue(next)
    onChange?.(next)
  }

  function handleTextChange(raw: string) {
    // Buchstaben/Sonderzeichen ignorieren: Textfeld bleibt unverändert, kein `onChange`.
    if (/[^\d.]/.test(raw)) {
      return
    }
    const masked = maskDateInput(raw)
    setText(masked)
    // Unvollständiges oder ungültiges Datum (z. B. 31.02.) leert den Wert, der Text bleibt zum
    // Weitertippen stehen — so greift z. B. die Pflichtfeld-Validierung des Formulars.
    const parsed =
      masked.length === DATE_FORMAT.length
        ? parse(masked, DATE_FORMAT, new Date())
        : undefined
    emit(parsed && isValid(parsed) ? parsed : undefined)
  }

  function selectFromCalendar(date: Date | undefined) {
    setText(date ? format(date, DATE_FORMAT) : "")
    emit(date)
    setOpen(false)
  }

  return (
    <div
      data-slot="input-date-picker"
      className={cn(
        "flex h-9 w-full items-center gap-2 rounded-full border-2 border-neutrals-v2-200 bg-backgrounds-primary py-2 pr-3 pl-4 text-sm leading-5 transition-colors has-[[data-slot=input-date-picker-field]:disabled]:cursor-not-allowed has-[[data-slot=input-date-picker-field]:focus:not([aria-invalid=true])]:border-brand-blue-100 has-[[data-slot=input-date-picker-field][aria-invalid=true]]:border-error-red-200",
        className
      )}
    >
      <input
        id={id}
        data-slot="input-date-picker-field"
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={text}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        onChange={(event) => handleTextChange(event.target.value)}
        className="min-w-0 flex-1 bg-transparent text-labels-primary outline-none placeholder:text-labels-secondary disabled:cursor-not-allowed disabled:text-labels-tertiary"
        {...props}
      />
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          // Beim Öffnen immer auf den Monat des aktuellen Werts springen — `Calendar`
          // kennt kein `defaultMonth`, nur kontrolliertes `month`/`onMonthChange`.
          if (next) {
            setCalendarMonth(value ?? new Date())
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Kalender öffnen"
            data-slot="input-date-picker-trigger"
            className="flex shrink-0 items-center justify-center text-labels-primary outline-none disabled:cursor-not-allowed disabled:text-labels-tertiary"
          >
            <FontAwesomeIcon icon={faCalendar} className="size-[18px]" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          // Desktop: `Calendar` bringt Rahmen/Schatten selbst mit, das Popover bleibt unsichtbar.
          // Mobile (Figma „Drawer Date Picker"): Drawer behält seinen weißen Hintergrund — sonst
          // schwebt der Grabber außerhalb über dem Overlay —, Grabber 12px unter der Oberkante,
          // 16px Abstand zum Kalender (Header-`pb-1` + Drawer-`gap-3`), 16px Seitenrand.
          className="max-md:gap-4 max-md:px-4 max-md:pt-3 md:w-fit md:border-none md:bg-transparent md:p-0 md:shadow-none"
        >
          <Calendar
            mode="single"
            selected={value}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            onSelect={selectFromCalendar}
            onTodayClick={(today) => selectFromCalendar(startOfDay(today))}
            // Mobile: Kalender liegt flach im Drawer (kein eigener Rahmen/Schatten), volle Breite.
            className="max-md:w-full max-md:gap-4 max-md:rounded-none max-md:border-none max-md:bg-transparent max-md:p-0 max-md:drop-shadow-none"
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
InputDatePicker.displayName = "InputDatePicker"

export { InputDatePicker }
